import React, { useRef, useEffect } from 'react';
import { Editor } from '@toast-ui/react-editor';
import '@toast-ui/editor/dist/toastui-editor.css';

const ProductEditor = ({
    initialContent = '',
    contentImages = [],
    selectedContentImages = new Set(),
    onContentImageToggle,
    onSave,
    onCancel,
    height = '600px'
}) => {
const editorRef = useRef(null);

// 에디터 초기 내용 생성 함수
const getInitialEditorContent = () => {
    if (initialContent) {
    return initialContent;
    }
    
    if (contentImages && contentImages.length > 0) {
    // 선택된 content_images만 마크다운으로 변환
    const selectedImages = contentImages.filter((_, index) => selectedContentImages.has(index));
    return selectedImages
        .map(imageUrl => `![상세 이미지](${imageUrl})`)
        .join('\n\n');
    }
    
    return '';
};

// 에디터 로드 시 스크롤을 맨 위로 이동
useEffect(() => {
    if (editorRef.current) {
    setTimeout(() => {
        const editorElement = editorRef.current.getInstance().getEditorElements();
        if (editorElement && editorElement.mdEditor) {
        // 스크롤을 맨 위로 이동
        editorElement.mdEditor.scrollTop = 0;
        editorElement.mdEditor.scrollTo(0, 0);
        
        // 포커스를 맨 위로 이동
        editorElement.mdEditor.focus();
        }
    }, 150);
    }
}, []);

// 상세 이미지 선택/해제 핸들러
const handleContentImageToggle = (index) => {
    if (onContentImageToggle) {
    onContentImageToggle(index);
    }
    
    // 에디터 내용 업데이트 (토글된 상태 반영)
    if (editorRef.current) {
    // 토글된 상태를 반영한 새로운 선택된 이미지들
    const newSelectedImages = new Set(selectedContentImages);
    if (newSelectedImages.has(index)) {
        newSelectedImages.delete(index);
    } else {
        newSelectedImages.add(index);
    }
    
    const selectedImages = contentImages.filter((_, idx) => newSelectedImages.has(idx));
    const imageMarkdown = selectedImages
        .map(imageUrl => `![상세 이미지](${imageUrl})`)
        .join('\n\n');
    
    const editor = editorRef.current.getInstance();
    editor.setMarkdown(imageMarkdown);
    
    // 에디터 스크롤을 맨 위로 강제 이동
    setTimeout(() => {
        const editorElement = editorRef.current.getInstance().getEditorElements();
        if (editorElement && editorElement.mdEditor) {
        // 스크롤을 맨 위로 이동
        editorElement.mdEditor.scrollTop = 0;
        editorElement.mdEditor.scrollTo(0, 0);
        
        // 포커스를 맨 위로 이동
        editorElement.mdEditor.focus();
        }
    }, 100);
    }
};

// 저장 핸들러
const handleSave = () => {
    if (!editorRef.current) return;
    
    const htmlContent = editorRef.current.getInstance().getHTML();
    
    // 선택된 content_images만 필터링
    const filteredContentImages = contentImages 
    ? contentImages.filter((_, index) => selectedContentImages.has(index))
    : [];
    
    if (onSave) {
    onSave(htmlContent, filteredContentImages);
    }
};

return (
    <div className="space-y-4">
    {/* Toast UI Editor */}
    <div className="border border-gray-300 rounded-lg overflow-hidden">
        <Editor
        ref={editorRef}
        initialValue={getInitialEditorContent()}
        previewStyle="vertical"
        height={height}
        initialEditType="wysiwyg"
        useCommandShortcut={true}
        usageStatistics={false}
        hideModeSwitch={true}
        toolbarItems={[
            ['heading', 'bold', 'italic', 'strike'],
            ['hr', 'quote'],
            ['ul', 'ol', 'task', 'indent', 'outdent'],
            ['table', 'image', 'link'],
            ['code', 'codeblock'],
            ['scrollSync']
        ]}
        events={{
            load: () => {
            // 에디터 로드 시 스크롤을 맨 위로 이동
            setTimeout(() => {
                if (editorRef.current) {
                const editorElement = editorRef.current.getInstance().getEditorElements();
                if (editorElement && editorElement.mdEditor) {
                    // 스크롤을 맨 위로 이동
                    editorElement.mdEditor.scrollTop = 0;
                    editorElement.mdEditor.scrollTo(0, 0);
                    
                    // 포커스를 맨 위로 이동
                    editorElement.mdEditor.focus();
                }
                }
            }, 150);
            }
        }}
        />
    </div>

    {/* 상세 이미지 선택 영역 */}
    {contentImages && contentImages.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">상세 이미지 선택</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {contentImages.map((imageUrl, index) => (
            <div
                key={index}
                className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                selectedContentImages.has(index)
                    ? 'border-blue-500 ring-2 ring-blue-200'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleContentImageToggle(index)}
            >
                <img
                src={imageUrl}
                alt={`상세 이미지 ${index + 1}`}
                className="w-full h-24 object-cover"
                />
                {selectedContentImages.has(index) && (
                <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                    <div className="bg-blue-500 text-white rounded-full p-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    </div>
                </div>
                )}
            </div>
            ))}
        </div>
        </div>
    )}

    {/* 액션 버튼들 */}
    <div className="flex justify-end gap-3">
        <button
        onClick={onCancel}
        className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
        취소
        </button>
        <button
        onClick={handleSave}
        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
        저장
        </button>
    </div>
    </div>
);
};

export default ProductEditor;
